import React, { memo, SetStateAction, useState } from 'react'
import { snakeCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, Box as Column } from '@node-space/storybook-components/dist/Box'
import { TableRowV2 } from '@node-space/storybook-components/dist/components/SimpleTableV2'
import { Search } from '@node-space/storybook-components/dist/Search'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import NoSearchResults from 'components/NoSearchResults/NoSearchResults'
import { TableRowSkeleton } from 'components/Skeletons/TableRowSkeleton'
import { Description } from 'components/text/Description'
import { PathNames } from 'constants/General'
import { useWalletControllerContext } from 'hooks/useWalletControllerContext'
import { WalletTypes } from '../../../../types/types'
import type { TableSection } from '../../types'
import { WalletAction } from '../Modal/WalletModal'
import Submenu from '../Submenu'
import Balance from './Balance'
import Currency from './Currency'
import RowHeader from './RowHeader'

interface Props {
  sections: TableSection[]
  setWalletModalAction: React.Dispatch<SetStateAction<WalletAction>>
  handleEditWalletName: (walletId: string, walletName: string) => void
}

const WalletTable = ({ sections, setWalletModalAction, handleEditWalletName }: Props) => {
  const { showWalletTotal } = useFeatureFlags()
  const { isLoading, hasErrors, baseCurrency } = useWalletControllerContext()

  const { t } = useTranslation()
  const navigate = useNavigate()

  const [filteredSections, setFilteredSections] = useState<TableSection[]>()
  const [searchQuery, setSearchQuery] = useState<string | undefined>('')

  const filterItems = (query: string) => {
    setSearchQuery(query)
    setFilteredSections(
      query
        ? sections?.map(section => {
            return {
              ...section,
              items: section.items.filter(item => {
                return (
                  item.currency.code.includes(query.toUpperCase()) ||
                  item.description.toLowerCase().includes(query.toLowerCase())
                )
              }),
            }
          })
        : sections
    )
  }

  const convertWalletInfo = (type: WalletTypes): TableRowV2[] => {
    return (filteredSections || sections)
      .filter(section => section.items && section.items.length > 0 && section.id === type)?.[0]
      ?.items.map(wallet => {
        return {
          testid: `wallet-row-${snakeCase(wallet.description)}`,
          onClick: (e: { stopPropagation: () => void }) => {
            e?.stopPropagation()
            navigate(`${PathNames.WALLETS}/wallet-details/${wallet.id}?tab='recent-activity'`)
          },
          rowData: [
            {
              cellData: <Currency wallet={wallet} isEmoney={wallet?.isEmoney} />,
              testid: `wallet-${snakeCase(wallet.description)}-name`,
            },
            {
              cellData: <Balance wallet={wallet} baseCurrency={baseCurrency} />,
              align: 'end',
              testid: `wallet-${snakeCase(wallet.description)}-balance`,
            },
            {
              cellData: (
                <Submenu
                  wallet={wallet}
                  handleEditWalletName={handleEditWalletName}
                  setWalletModalAction={setWalletModalAction}
                  type={type}
                  key={wallet.id}
                />
              ),
              align: 'end',
              testid: `wallet-${snakeCase(wallet.description)}-popover-menu`,
            },
          ],
        }
      }) as TableRowV2[]
  }

  if (hasErrors) {
    return (
      <StatusCard status="error" headline={t('requestErrors.pageErrorIntro')} borderless>
        <Description>
          {`${t('requestErrors.comeBackLater')} `}
          <TextAction isWindowReload text={t('requestErrors.refreshPage')} />.
        </Description>
      </StatusCard>
    )
  }

  const hasWalletTypes = convertWalletInfo('FIAT') || convertWalletInfo('CRYPTO')

  return (
    <>
      <Column width="full" className="block mr-3">
        <Search
          searchTerm={searchQuery}
          size="sm"
          testid="filter-search-field"
          placeholderText={t('walletsPage.searchPlaceholder')}
          onChange={e => filterItems(e.target.value)}
        />
      </Column>
      {hasWalletTypes ? (
        <>
          <RowHeader
            testid="fiat-wallet-table"
            convertWalletInfo={convertWalletInfo}
            showWalletTotal={showWalletTotal}
            currencyType={'FIAT' as WalletTypes}
            key={'FIAT'}
          />
          <RowHeader
            testid="crypto-wallet-table"
            convertWalletInfo={convertWalletInfo}
            showWalletTotal={showWalletTotal}
            currencyType={'CRYPTO' as WalletTypes}
            key={'CRYPTO'}
          />
        </>
      ) : isLoading || !sections ? (
        <>
          <Box width="full">
            <TableRowSkeleton rows={8} />
          </Box>
        </>
      ) : (
        <NoSearchResults
          clearSearchCriteria={() => {
            setSearchQuery('')
            filterItems('')
          }}
        />
      )}
    </>
  )
}

export default memo(WalletTable)
